import React, { useMemo, useState } from "react";
import type { IntegrationKey } from "../../types";
import { COST_RATES } from "../../services/costRates";
import { formatZAR } from "../../services/plans";

interface Props {
  data: { day: string; byIntegration: Record<IntegrationKey, number> }[];
  series: IntegrationKey[];
  height?: number;
}

/**
 * Stacked area chart of cost over time, one band per integration. Drawn
 * as pure SVG so we don't pay for a charting library.
 */
export function StackedAreaChart({ data, series, height = 220 }: Props) {
  const VB_W = 1000;
  const VB_H = height;
  const PAD = { top: 12, right: 12, bottom: 24, left: 56 };
  const W = VB_W - PAD.left - PAD.right;
  const H = VB_H - PAD.top - PAD.bottom;

  const [hover, setHover] = useState<number | null>(null);

  const { stacks, max, totals } = useMemo(() => {
    const n = data.length;
    if (n === 0) return { stacks: [] as number[][], max: 0, totals: [] as number[] };
    // stacks[seriesIndex][dayIndex] = top-of-band value
    const cumulative = new Array(n).fill(0);
    const totals = data.map((d) => series.reduce((s, k) => s + (d.byIntegration[k] ?? 0), 0));
    const max = Math.max(1, ...totals);
    const stacks: number[][] = series.map((k) => {
      return data.map((d, i) => {
        cumulative[i] += d.byIntegration[k] ?? 0;
        return cumulative[i];
      });
    });
    return { stacks, max, totals };
  }, [data, series]);

  if (data.length === 0) {
    return <div className="empty-state"><h3>No usage in this range</h3></div>;
  }

  const x = (i: number) => (i / Math.max(1, data.length - 1)) * W + PAD.left;
  const y = (v: number) => PAD.top + H - (v / max) * H;

  // gridlines
  const gridY = [0, 0.25, 0.5, 0.75, 1].map((p) => max * p);

  // build polygons bottom-up
  const polys = stacks.map((stack, si) => {
    const prevStack = si === 0 ? new Array(data.length).fill(0) : stacks[si - 1];
    const topPts = stack.map((v, i) => `${x(i)},${y(v)}`).join(" ");
    const botPts = prevStack.map((v, i) => `${x(i)},${y(v)}`).reverse().join(" ");
    return `${topPts} ${botPts}`;
  });

  return (
    <div style={{ width: "100%", position: "relative" }}>
      <svg viewBox={`0 0 ${VB_W} ${VB_H}`} role="img" style={{ width: "100%", height: "auto" }}>
        <defs>
          {series.map((k) => (
            <linearGradient key={k} id={`area-${k}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={COST_RATES[k].color} stopOpacity={0.85} />
              <stop offset="100%" stopColor={COST_RATES[k].color} stopOpacity={0.35} />
            </linearGradient>
          ))}
        </defs>

        {/* gridlines */}
        {gridY.map((g, i) => (
          <g key={i}>
            <line
              x1={PAD.left} x2={VB_W - PAD.right}
              y1={y(g)} y2={y(g)}
              stroke="rgba(255,255,255,0.06)" strokeWidth={1}
            />
            <text
              x={PAD.left - 8} y={y(g) + 3}
              fontSize={10} fill="var(--text-mute)" textAnchor="end"
            >
              {fmtCentsShort(g)}
            </text>
          </g>
        ))}

        {/* stacked areas */}
        {polys.map((pts, i) => (
          <polygon
            key={series[i]}
            points={pts}
            fill={`url(#area-${series[i]})`}
            stroke={COST_RATES[series[i]].color}
            strokeWidth={0.8}
            opacity={0.95}
          />
        ))}

        {/* X axis ticks (every ~7 days) */}
        {data.map((d, i) => {
          const step = Math.max(1, Math.floor(data.length / 6));
          if (i % step !== 0 && i !== data.length - 1) return null;
          return (
            <text
              key={d.day}
              x={x(i)} y={VB_H - 6}
              fontSize={10} fill="var(--text-mute)" textAnchor="middle"
            >
              {d.day.slice(5)}
            </text>
          );
        })}

        {/* hover crosshair */}
        {hover != null && (
          <g>
            <line
              x1={x(hover)} x2={x(hover)}
              y1={PAD.top} y2={PAD.top + H}
              stroke="var(--text)" strokeOpacity={0.25}
            />
          </g>
        )}

        {/* mouse capture */}
        <rect
          x={PAD.left} y={PAD.top} width={W} height={H}
          fill="transparent"
          onMouseLeave={() => setHover(null)}
          onMouseMove={(e) => {
            const rect = (e.target as SVGRectElement).getBoundingClientRect();
            const px = e.clientX - rect.left;
            const idx = Math.round((px / rect.width) * (data.length - 1));
            setHover(Math.max(0, Math.min(data.length - 1, idx)));
          }}
        />
      </svg>

      {hover != null && (
        <div
          style={{
            position: "absolute",
            top: 6,
            left: 64 + (hover / Math.max(1, data.length - 1)) * (100 - 12) + "%",
            transform: "translateX(-50%)",
            pointerEvents: "none"
          }}
        >
          <div
            style={{
              background: "rgba(17,17,42,0.95)",
              border: "1px solid var(--border-strong)",
              borderRadius: 8,
              padding: "8px 12px",
              fontSize: 12,
              minWidth: 160,
              boxShadow: "var(--shadow-lg)"
            }}
          >
            <div style={{ color: "var(--text)", fontWeight: 600, marginBottom: 4 }}>{data[hover].day}</div>
            {series.map((k) => {
              const c = data[hover].byIntegration[k] ?? 0;
              if (c === 0) return null;
              return (
                <div key={k} className="row" style={{ gap: 6, justifyContent: "space-between" }}>
                  <span className="row" style={{ gap: 6 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: COST_RATES[k].color }} />
                    <span style={{ color: "var(--text-dim)" }}>{COST_RATES[k].label}</span>
                  </span>
                  <span className="tnum" style={{ color: "var(--text)" }}>{formatZAR(c)}</span>
                </div>
              );
            })}
            <div style={{ borderTop: "1px solid var(--border)", marginTop: 6, paddingTop: 6, display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "var(--text-mute)" }}>Total</span>
              <span className="tnum" style={{ color: "var(--text)", fontWeight: 700 }}>{formatZAR(totals[hover])}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function fmtCentsShort(cents: number): string {
  const r = cents / 100;
  if (r >= 1000) return `R${Math.round(r / 1000)}k`;
  return `R${Math.round(r)}`;
}
